import Modal from 'react-modal';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import View from '@picsart/view/View';
import IconCrossLarge from '@picsart/icons/IconCrossLarge';
import { Spacings } from '@picsart/cascade';
import { Button, ButtonSizeSM, ButtonSkinTertiary } from '@picsart/button';

import useMemoSelector from 'hooks/useMemoSelector';
import { getPreviewImage } from 'store/selectors/ui';
import uiSlice from 'store/ui';
import useBatchData from 'pages/Batch/sections/Context/useBatchData';
import { BATCH_TOOLS } from 'pages/Batch/configs';

import useStyles from './styles';
import { PreviewModalContextProvider } from './Context';
import { BasicModalContent, BeforeAfterModalContent, CropModalContent, RemoveBackgroundModalContent, ResizeModalContent } from './Modals';

const PreviewModal = () => {
  const dispatch = useDispatch();
  const previewImage = useMemoSelector(getPreviewImage);
  const { activeTool, setSelectedKeys, setShowDeleteItemConfirmModal } = useBatchData();

  const classes = useStyles();

  const handleClose = useCallback(() => {
    dispatch(uiSlice.actions.setPreviewImage(undefined));
  }, [dispatch]);

  const handleDelete = useCallback(() => {
    if (!previewImage) return;

    setSelectedKeys([previewImage.itemKey]);
    setShowDeleteItemConfirmModal(true);
    handleClose();
  }, [handleClose, previewImage, setSelectedKeys, setShowDeleteItemConfirmModal]);

  const modalContent = useMemo(() => {
    if (!previewImage) return;

    switch (activeTool) {
      case BATCH_TOOLS.enhance:
        return <BeforeAfterModalContent beforeImageSrc={previewImage.src} afterImageSrc={previewImage.secondSrc || ''} />;
      case BATCH_TOOLS.crop:
        return <CropModalContent imageSrc={previewImage.src} itemKey={previewImage.itemKey} onClose={handleClose} />;
      case BATCH_TOOLS.resize:
      case BATCH_TOOLS.removeBG_resize:
        return <ResizeModalContent imageSrc={previewImage.src} originalImageSrc={previewImage.secondSrc || ''} />;
      case BATCH_TOOLS.removeBG:
        return <RemoveBackgroundModalContent imageSrc={previewImage.src} />;
      default:
        return <BasicModalContent imageSrc={previewImage.src} onDeleteImage={handleDelete} withDeleteAction={false} />;
    }
  }, [activeTool, handleClose, handleDelete, previewImage]);

  return (
    <Modal
      overlayClassName={classes.modalOverlay}
      className={classes.modalStyles}
      isOpen={Boolean(previewImage)}
      onRequestClose={handleClose}
      shouldCloseOnOverlayClick
      shouldCloseOnEsc
    >
      <View justifyContent="flex-end" padding={Spacings.s12} paddingBlockEnd={Spacings.s0}>
        <Button className={classes.close} centerIcon={IconCrossLarge} size={ButtonSizeSM} skin={ButtonSkinTertiary} onClick={handleClose} />
      </View>
      <PreviewModalContextProvider>
        {modalContent}
      </PreviewModalContextProvider>
    </Modal>
  );
};

export default PreviewModal;
